import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  MessageCircle,
  MapPinned,
  FileText,
  ClipboardCheck,
  Hammer,
  CheckCircle2,
} from 'lucide-react';

const ServiceFlowSection = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const steps = [
    {
      icon: MessageCircle,
      title: 'お問い合わせ・ご相談',
      description:
        'メールフォームやLINEから、今の状況やお悩みをお聞かせください。まだ具体的に決まっていなくても大丈夫です。',
    },
    {
      icon: MapPinned,
      title: '現地確認・ヒアリング',
      description:
        'お墓の場所・状態・区画、周辺環境などを確認します。ご家族のご希望やご予算感も、この段階で丁寧に伺います。',
    },
    {
      icon: FileText,
      title: 'お見積もりのご提示',
      description:
        '墓石解体の内容、市営霊園や寺院での条件、改葬許可申請の有無などを踏まえ、分かりやすいお見積もりを作成します。',
    },
    {
      icon: ClipboardCheck,
      title: '隣接する墓石の状況',
      description:
        '役所への改葬許可申請が必要な場合、書類の準備や流れについてサポートいたします。',
    },
    {
      icon: Hammer,
      title: '墓石解体・撤去作業',
      description:
        '周囲への安全や音、近隣への配慮を大切にしながら、丁寧に作業を行います。',
    },
    {
      icon: CheckCircle2,
      title: '更地への整地・完了報告',
      description: '区画をきれいに整地し、写真などで完了のご報告をいたします。',
    },
  ];

  return (
    <section id="service" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-7xl mx-auto">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-stone-800 mb-4">
            墓じまいから新しい供養の形まで
          </h2>
          <p className="text-xl text-stone-600">
            分かりやすい流れで丁寧にご案内します
          </p>
        </motion.div>

        {/* Flow Steps */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-stone-800 mb-8 text-center">
            墓じまいの基本の流れ
          </h3>
          <p className="text-stone-600 max-w-3xl mx-auto text-center mb-12 leading-relaxed">
            初めての墓じまいは、「何から始めればよいのか」「どこまで自分で準備するのか」が分かりづらいものです。
            私たちは、できるだけご負担を減らせるよう、次のような流れで進めていきます。
          </p>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {steps.map((step, index) => {
              const Icon = step.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: 0.2 + index * 0.1 }}
                  className="relative"
                >
                  <div className="bg-gradient-to-br from-stone-50 to-white rounded-xl p-6 border-2 border-stone-200 hover:border-amber-400 transition-all duration-300 h-full flex flex-col">
                    <div className="flex items-start gap-4 mb-4">
                      <div className="flex-shrink-0 w-12 h-12 bg-amber-600 rounded-lg flex items-center justify-center">
                        <Icon className="w-6 h-6 text-white" />
                      </div>
                      <div className="flex-shrink-0 w-8 h-8 bg-stone-200 rounded-full flex items-center justify-center font-bold text-stone-700">
                        {index + 1}
                      </div>
                    </div>
                    <h4 className="text-lg font-bold text-stone-800 mb-3">
                      {step.title}
                    </h4>
                    <p className="text-stone-600 leading-relaxed flex-grow">
                      {step.description}
                    </p>
                  </div>
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-amber-400 z-10"></div>
                  )}
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ServiceFlowSection;
